// ============================================================================
// MÉTRICAS (C4) — la pantalla del DUEÑO, no la del agente.
//
// Criterio para elegir qué se muestra: cada número tiene que responder una
// pregunta que el dueño se hace de verdad. Nada de cifras bonitas que no
// cambian ninguna decisión.
//
//   "¿cuánto trabajo me quita el bot?"      → atendidas solo por el bot
//   "¿me está costando mucho?"              → costo de IA del periodo
//   "¿estoy dejando clientes tirados?"      → seguimientos pendientes
//   "¿cómo va el embudo?"                   → contactos por etapa
//   "¿cuántos 🔥 llegan y cuánto tardan?"    → el embudo calificado (T-CAL-9)
//   "¿el audio vende más que el texto?"      → audio contra texto (T-CAL-22)
//
// El costo se muestra en pesos además de dólares: "US$0,03" no le dice nada a
// nadie en Manizales.
// ============================================================================
import { useCallback, useEffect, useState } from "react";
import { Bot, MessageSquare, TrendingUp, UserPlus } from "lucide-react";
import { Aviso, Chip, SkeletonLista, Vacio } from "./piezas.tsx";
import {
  cuadroEmbudo, embudoConteo, explicarError, leerMoverAutomatico, leerVozResultados, metricasResumen,
  type CuadroEmbudo, type EtapaConteo, type Metricas as Datos,
} from "./datos.ts";
import type { ContextoPanel, FilaVozResultados, VozResultados } from "./tipos.ts";

const PERIODOS = [
  { d: 7, t: "7 días" },
  { d: 30, t: "30 días" },
  { d: 90, t: "3 meses" },
];

/** Tasa de referencia para traducir el costo a pesos. Es una ORIENTACIÓN, no
 *  contabilidad: por eso se muestra "≈" y se dice de dónde sale.
 *  ⚠️ La usa también el hilo (T-CAL-12: el costo de la nota de voz del bot): una
 *  sola tasa, o el panel diría dos precios distintos para el mismo dólar. */
export const COP_POR_USD = 4000;

function Cifra({
  I, valor, etiqueta, detalle, tono = "neutro",
}: {
  I: typeof Bot; valor: string; etiqueta: string; detalle?: string;
  tono?: "neutro" | "marca" | "alerta";
}) {
  return (
    <div className="border border-borde rounded-xl p-3.5 bg-superficie">
      <div className="flex items-center gap-2 text-tinta-2">
        <I size={14} />
        <span className="text-[12.5px]">{etiqueta}</span>
      </div>
      <p className={`text-[26px] font-semibold tracking-tight mt-1 cifra
        ${tono === "marca" ? "text-marca" : tono === "alerta" ? "text-marca" : ""}`}>
        {valor}
      </p>
      {detalle && <p className="text-[12px] text-tinta-2 mt-0.5">{detalle}</p>}
    </div>
  );
}

function BarraEmbudo({ etapas }: { etapas: EtapaConteo[] }) {
  const total = etapas.reduce((s, e) => s + e.contactos, 0);
  if (total === 0) {
    return <p className="text-[13px] text-tinta-2">Todavía no hay contactos en el embudo.</p>;
  }
  return (
    <ul className="space-y-2">
      {etapas.map((e) => {
        const pct = Math.round((e.contactos / total) * 100);
        return (
          <li key={e.etapa_id}>
            <div className="flex items-baseline gap-2 text-[13px]">
              <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: e.color }} />
              <span className="truncate">{e.nombre}</span>
              <span className="ml-auto cifra text-tinta-2">{e.contactos}</span>
            </div>
            <div className="h-1.5 rounded-full bg-tenue mt-1 overflow-hidden">
              <div className="h-full rounded-full transition-[width] duration-500"
                style={{ width: `${pct}%`, background: e.color }} />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

// ── T-CAL-9 · EL EMBUDO CALIFICADO (R-MED-01 · R-MED-02 · I-142) ──────────────
// Las seis cifras como embudo en fila y cuánto tardó el asesor en tomar al 🔥.
// ⚠️ Calificados, 🔥, pasados y tomados se REGISTRAN desde la migración 0077: antes
//    no existían. Si el primer registro es posterior al inicio del periodo, se dice
//    («cuenta desde…»); jamás se rellena lo de antes (el plan lo prohíbe).
const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
function fechaCorta(iso: string): string {
  const d = new Date(iso);
  return `${d.getDate()}-${MESES[d.getMonth()]}`;
}
const minutos = (m: number) =>
  `${m.toLocaleString("es-CO", { maximumFractionDigits: 1 })} min`;

function EmbudoCalificado({ c }: { c: CuadroEmbudo }) {
  const pasos: { k: string; valor: number; etiqueta: string }[] = [
    { k: "entraron", valor: c.entraron, etiqueta: "Entraron" },
    { k: "calificados", valor: c.calificados, etiqueta: "Calificados" },
    { k: "calientes", valor: c.calientes, etiqueta: "🔥 Calientes" },
    { k: "pasados", valor: c.pasados, etiqueta: "Pasados al asesor" },
    // ⭐ r1 (H-1): «tomado» = una persona le CONTESTÓ al 🔥 o pulsó «Tomar el chat».
    { k: "tomados", valor: c.tomados, etiqueta: "Le contestó o lo tomó un asesor" },
    { k: "vendidos", valor: c.vendidos, etiqueta: "Vendidos" },
  ];
  const cuentaDesde = c.contando_desde === null
    || (c.desde !== "" && new Date(c.contando_desde).getTime() > new Date(c.desde).getTime());
  return (
    <section className="border border-borde rounded-xl p-4 bg-superficie" aria-label="El embudo calificado">
      <h2 className="text-[14px] font-medium mb-3">El embudo calificado</h2>
      <ol className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {pasos.map((p, i) => (
          <li key={p.k} data-paso={p.k}
            className="relative rounded-lg bg-tenue/60 px-2.5 py-2 min-w-0">
            <p className="text-[22px] font-semibold tracking-tight cifra leading-none">{p.valor}</p>
            <p className="text-[12px] text-tinta-2 mt-1 leading-tight">{p.etiqueta}</p>
            {i < pasos.length - 1 && (
              <span aria-hidden className="hidden sm:block absolute -right-2 top-1/2 -translate-y-1/2 text-tinta-2 text-[12px]">→</span>
            )}
          </li>
        ))}
      </ol>
      <div className="flex items-center gap-2 flex-wrap mt-3 text-[13.5px]">
        <p data-cifra="mediana">
          {c.minutos_mediana_tomar === null
            ? "Todavía ningún 🔥 contestado o tomado en este periodo."
            : <>El asesor tardó <strong className="cifra">{minutos(c.minutos_mediana_tomar)}</strong> (mediana) en contestar o tomar al 🔥
                {c.minutos_max_tomar !== null && <span className="text-tinta-2"> · el que más, {minutos(c.minutos_max_tomar)}</span>}</>}
        </p>
        {c.pasados_sin_tomar > 0 && (
          <Chip tono="alerta">{c.pasados_sin_tomar} {c.pasados_sin_tomar === 1 ? "espera" : "esperan"} asesor</Chip>
        )}
      </div>
      {cuentaDesde && (
        <p data-cuenta-desde className="text-[12.5px] text-tinta-2 mt-2">
          {c.contando_desde === null
            ? "Calificados, 🔥, pasados y tomados se cuentan desde que llegue el primero: antes no se registraban."
            : `Cuenta desde el ${fechaCorta(c.contando_desde)}: antes no se registraba (calificados, 🔥, pasados y tomados).`}
        </p>
      )}
    </section>
  );
}

// ── T-CAL-22 · AUDIO CONTRA TEXTO (I-184) ──────────────────────────────────────
// La pregunta de Cristhian: ¿el audio se justifica? Métrica de la consulta externa
// (actas/CONSULTA-EXTERNA-VOZ-Y-DUENO-27sep.md §C): ¿el cliente respondió en 24 h?, grupo
// `voz` contra `texto` (la A/B de T-CAL-20), ¿escuchó la nota? y el gasto.
// ⛔ Los momentos NO se suman bajo «clientes»: un contacto con recomendación y compra la
//    misma semana contaría dos veces y el % mezclaría dos anclas. Una línea por momento.
//    Sumar SEMANAS sí vale: cada momento suena una vez por contacto (I-177).
const MOMENTOS: { k: FilaVozResultados["momento"]; t: string }[] = [
  { k: "recomendacion", t: "Recomendación" },
  { k: "compra", t: "Compra" },
];
const GRUPOS: { k: FilaVozResultados["grupo_ab"]; t: string }[] = [
  { k: "voz", t: "Con audio" },
  { k: "texto", t: "Solo texto" },
];
export const FRASE_AZAR =
  "Con menos de ~400 clientes por grupo la diferencia puede ser azar: mirar la tendencia, no una semana sola.";
// ⚠️ Espacio DURO en «24 h» y «%»: a 375 px el «h» y el «%» quedaban solos en la línea siguiente.
const pct = (a: number, b: number) => `${Math.round((a / b) * 100)}\u00a0%`;
const usd = (v: number) =>
  `$${v.toLocaleString("es-CO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`;
function semanaCorta(ymd: string): string {
  const [, m, d] = ymd.split("-").map(Number);
  return `${d}-${MESES[m - 1]}`;
}

/** Suma las filas de un mismo (grupo, momento). Costo: null si ninguna lo trae. */
function sumar(filas: FilaVozResultados[]): FilaVozResultados | null {
  if (filas.length === 0) return null;
  const costos = filas.map((f) => f.costo_usd).filter((c): c is number => c !== null);
  return {
    grupo_ab: filas[0].grupo_ab, momento: filas[0].momento,
    clientes: filas.reduce((a, f) => a + f.clientes, 0),
    respondieron_24h: filas.reduce((a, f) => a + f.respondieron_24h, 0),
    notas: filas.reduce((a, f) => a + f.notas, 0),
    escuchadas: filas.reduce((a, f) => a + f.escuchadas, 0),
    costo_usd: costos.length ? costos.reduce((a, c) => a + c, 0) : null,
  };
}

/** «12 clientes · 42 % respondió en 24 h · 80 % escuchó · $0,12 USD» (lo que haya). */
function Celda({ f }: { f: FilaVozResultados | null }) {
  if (!f || f.clientes === 0) return <span className="text-tinta-2">—</span>;
  const partes = [
    `${f.clientes} ${f.clientes === 1 ? "cliente" : "clientes"}`,
    `${pct(f.respondieron_24h, f.clientes)} respondió en 24\u00a0h`,
  ];
  if (f.grupo_ab === "voz" && f.notas > 0) partes.push(`${f.escuchadas} de ${f.notas} ${f.notas === 1 ? "audio escuchado" : "audios escuchados"}`); // H2 r1: sobre NOTAS, no clientes
  if (f.costo_usd !== null) partes.push(usd(f.costo_usd));
  return <span className="cifra">{partes.join(" · ")}</span>;
}

/** Una tabla momento × grupo. En el celular (< sm) cada momento se APILA: su nombre y
 *  debajo una línea por grupo con su rótulo; sin scroll horizontal. */
function TablaGrupos({ filas, titulo, marca }: { filas: FilaVozResultados[]; titulo: string; marca: string }) {
  const momentos = MOMENTOS.filter((m) => filas.some((f) => f.momento === m.k));
  return (
    <div data-semana={marca} className="rounded-lg bg-tenue/60 px-3 py-2.5 min-w-0">
      <p className="text-[13px] font-medium mb-1.5">{titulo}</p>
      <div className="hidden sm:grid grid-cols-[8.5rem_1fr_1fr] gap-x-3 text-[11.5px] text-tinta-2 mb-1">
        <span>Momento</span>{GRUPOS.map((g) => <span key={g.k}>{g.t}</span>)}
      </div>
      <ul className="space-y-1.5">
        {momentos.map((m) => (
          <li key={m.k} data-momento={m.k}
            className="grid grid-cols-1 sm:grid-cols-[8.5rem_1fr_1fr] gap-x-3 gap-y-0.5 text-[13px] min-w-0">
            <span className="font-medium">{m.t}</span>
            {GRUPOS.map((g) => (
              <span key={g.k} data-grupo={g.k} className="min-w-0 break-words">
                <span className="sm:hidden text-tinta-2">{g.t}: </span>
                <Celda f={sumar(filas.filter((f) => f.momento === m.k && f.grupo_ab === g.k))} />
              </span>
            ))}
          </li>
        ))}
      </ul>
    </div>
  );
}

function AudioContraTexto({ r }: { r: VozResultados }) {
  const conDatos = r.semanas.filter((s) => s.filas.some((f) => f.clientes > 0));
  const todas = conDatos.flatMap((s) => s.filas);
  return (
    <section className="border border-borde rounded-xl p-4 bg-superficie" aria-label="Audio contra texto">
      <h2 className="text-[14px] font-medium mb-3">Audio contra texto</h2>
      {conDatos.length === 0
        ? <p className="text-[13px] text-tinta-2">Todavía no hay clientes en la comparación.</p>
        : (
          <div className="space-y-2.5">
            <TablaGrupos filas={todas} marca="total" titulo={`Últimas ${r.semanas.length} semanas`} />
            {conDatos.map((s) => (
              <TablaGrupos key={s.semana} filas={s.filas} marca={s.semana}
                titulo={`Semana del ${semanaCorta(s.semana)}${s.incompleta ? " · en curso" : ""}`} />
            ))}
          </div>
        )}
      <p data-frase-azar className="text-[12.5px] text-tinta-2 mt-3">{FRASE_AZAR}</p>
    </section>
  );
}

export function Metricas({ ctx }: { ctx: ContextoPanel }) {
  const { sb, tenantId, rol } = ctx;
  // T-CAL-22 · I-184 · el cuadro de la voz lo ven el dueño y Victorix (ENUMERADOS: un rol
  // nuevo no entra solo). Al agente ni se le pide: la base le diría 42501.
  const veVoz = rol === "dueno" || rol === "victorix";
  const [voz, setVoz] = useState<VozResultados | null>(null);
  const [errorVoz, setErrorVoz] = useState<string | null>(null);
  const [dias, setDias] = useState(30);
  const [datos, setDatos] = useState<Datos | null>(null);
  const [etapas, setEtapas] = useState<EtapaConteo[]>([]);
  // T-CAL-9 · null = el negocio no califica (el bloque no se pinta).
  const [cuadro, setCuadro] = useState<CuadroEmbudo | null>(null);
  const [errorCuadro, setErrorCuadro] = useState<string | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const recargar = useCallback(async () => {
    setCargando(true);
    // ⚠️ El cuadro va APARTE: si su RPC falla, el resto de la pantalla se ve igual.
    //    Solo se pide si el negocio califica (`mover_automatico` ≠ null, T-CAL-19).
    // ⚠️ T-CAL-22 · el cuadro de la voz, igual: aparte y sin tumbar Métricas.
    const pedirVoz = (async () => {
      if (!veVoz) { setVoz(null); setErrorVoz(null); return; }
      try {
        setVoz(await leerVozResultados(sb, tenantId, 8));
        setErrorVoz(null);
      } catch (e) {
        setVoz(null); setErrorVoz(explicarError(e));
      }
    })();
    const pedirCuadro = (async () => {
      try {
        const califica = await leerMoverAutomatico(sb, tenantId);
        setCuadro(califica === null ? null : await cuadroEmbudo(sb, tenantId, dias));
        setErrorCuadro(null);
      } catch (e) {
        setCuadro(null); setErrorCuadro(explicarError(e));
      }
    })();
    try {
      const [m, e] = await Promise.all([
        metricasResumen(sb, tenantId, dias),
        embudoConteo(sb, tenantId),
      ]);
      setDatos(m); setEtapas(e); setError(null);
    } catch (e) {
      setError(explicarError(e));
    } finally {
      await pedirCuadro;
      await pedirVoz;
      setCargando(false);
    }
  }, [sb, tenantId, dias, veVoz]);

  useEffect(() => { void recargar(); }, [recargar]);

  const pctBot = datos && datos.conversaciones > 0
    ? Math.round((datos.atendidas_solo_bot / datos.conversaciones) * 100)
    : 0;
  const cop = datos ? Math.round(datos.costo_ia_usd * COP_POR_USD) : 0;

  return (
    <div className="h-full flex flex-col min-w-0">
      <header className="shrink-0 px-4 sm:px-6 pt-4 pb-3 border-b border-borde-suave">
        <h1 className="text-[19px] font-semibold tracking-tight">Métricas</h1>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          {PERIODOS.map((p) => (
            <button key={p.d} onClick={() => setDias(p.d)}
              aria-pressed={dias === p.d}
              className={`text-[13px] px-3 py-1.5 rounded-full border transition cursor-pointer
                ${dias === p.d
                  ? "bg-tinta text-superficie border-tinta"
                  : "border-borde bg-superficie hover:border-marca/40"}`}>
              {p.t}
            </button>
          ))}
        </div>
      </header>

      <div className="flex-1 overflow-y-auto scroll-fino px-4 sm:px-6 py-4">
        <div className="max-w-[820px] mx-auto w-full space-y-5">
          {error && <Aviso tono="peligro" onCerrar={() => setError(null)}>{error}</Aviso>}
          {cargando && !datos && <SkeletonLista filas={4} />}

          {datos && datos.conversaciones === 0 && (
            <Vacio titulo="Todavía no hay nada que medir">
              Cuando empiecen a llegar conversaciones, aquí verás cuánto atiende el bot solo, cuánto
              cuesta y cómo va el embudo.
            </Vacio>
          )}

          {datos && datos.conversaciones > 0 && (
            <>
              <section className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
                <Cifra I={Bot} valor={`${pctBot} %`} tono="marca"
                  etiqueta="Lo resolvió el bot solo"
                  detalle={`${datos.atendidas_solo_bot} de ${datos.conversaciones} conversaciones`} />
                <Cifra I={MessageSquare} valor={String(datos.conversaciones)}
                  etiqueta="Conversaciones"
                  detalle={`${datos.pasaron_a_humano} pasaron a un asesor`} />
                <Cifra I={UserPlus} valor={String(datos.contactos_nuevos)}
                  etiqueta="Clientes nuevos"
                  detalle={`${datos.mensajes_recibidos} mensajes recibidos`} />
                <Cifra I={TrendingUp} valor={`$${cop.toLocaleString("es-CO")}`}
                  etiqueta="Costo del bot"
                  detalle={`≈ US$${datos.costo_ia_usd.toFixed(4)} · a $${COP_POR_USD.toLocaleString("es-CO")}/USD`} />
              </section>

              {cuadro && <EmbudoCalificado c={cuadro} />}
              {errorCuadro && (
                <Aviso tono="peligro" onCerrar={() => setErrorCuadro(null)}>
                  No se pudo leer el embudo calificado: {errorCuadro}
                </Aviso>
              )}

              {voz && <AudioContraTexto r={voz} />}
              {errorVoz && (
                <Aviso tono="peligro" onCerrar={() => setErrorVoz(null)}>
                  No se pudo leer el cuadro de audio contra texto: {errorVoz}
                </Aviso>
              )}

              <section className="border border-borde rounded-xl p-4 bg-superficie">
                <h2 className="text-[14px] font-medium mb-3">El embudo hoy</h2>
                <BarraEmbudo etapas={etapas} />
              </section>

              <section className="border border-borde rounded-xl p-4 bg-superficie">
                <h2 className="text-[14px] font-medium mb-2">Seguimientos</h2>
                <div className="flex items-center gap-2 flex-wrap text-[13.5px]">
                  <Chip tono="neutro">{datos.seguimientos_cumplidos} cumplidos</Chip>
                  {datos.seguimientos_pendientes > 0
                    ? <Chip tono="alerta">{datos.seguimientos_pendientes} sin atender</Chip>
                    : <Chip tono="neutro">nada pendiente</Chip>}
                </div>
                <p className="text-[12.5px] text-tinta-2 mt-2">
                  Cada seguimiento sin atender es un cliente que quedó esperando una respuesta que se
                  le prometió.
                </p>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
